import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import type { NavGroup } from '@/config/navigation';
import { useFeatureModule } from '@/hooks/useFeatureModule';
import { ThemeToggle } from '@/components/ThemeToggle';
import { ViewSwitcher } from '@/components/ViewSwitcher';
import { countBadge } from '@/styles/badges';

interface MobileNavProps {
  isOpen: boolean;
  onClose: () => void;
  groups: NavGroup[];
  userInfo?: {
    email: string;
    role: string;
  };
  onLogout?: () => void;
  viewMode?: 'admin' | 'teacher' | 'support_staff';
  onViewModeSwitch?: (mode: 'admin' | 'teacher' | 'support_staff') => void;
}

export const MobileNav: React.FC<MobileNavProps> = ({
  isOpen,
  onClose,
  groups,
  userInfo,
  onLogout,
  viewMode,
  onViewModeSwitch,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const primaryColor = 'var(--primary-color)';
  const pathnameOnOpenRef = useRef(location.pathname);
  const savedScrollRef = useRef(0);

  // Call hooks at top level (React Rules of Hooks)
  const { isEnabled: attendanceEnabled } = useFeatureModule('attendance');
  const { isEnabled: parentPortalEnabled } = useFeatureModule('parentPortal');
  // Intentional Layer-1-only: SMS usage/billing stays visible when the school pauses SMS via sms_enabled
  const { isAllowed: smsEnabled } = useFeatureModule('sms');
  const { isEnabled: messagingEnabled } = useFeatureModule('messaging');

  // Map for lookup
  const moduleStatus: Record<'attendance' | 'parentPortal' | 'sms' | 'messaging', boolean> = {
    attendance: attendanceEnabled,
    parentPortal: parentPortalEnabled,
    sms: smsEnabled,
    messaging: messagingEnabled,
  };

  // Filter groups based on module access, and filter links within groups
  const visibleNavGroups = groups
    .filter((group) => !group.requiresModule || moduleStatus[group.requiresModule])
    .map((group) => ({
      ...group,
      links: group.links.filter((link) => !link.requiresModule || moduleStatus[link.requiresModule]),
    }))
    .filter((group) => group.links.length > 0); // Remove groups with no visible links

  // Find which nav group contains the current pathname (strip query params from link.to)
  const findActiveGroupIndex = (pathname: string) =>
    visibleNavGroups.findIndex((group) =>
      group.links.some((link) => {
        const linkPath = link.to.split('?')[0];
        return pathname === linkPath || pathname.startsWith(linkPath + '/');
      })
    );

  // Track which sections are expanded (by group index)
  // Admins in admin view: auto-expand the active group, others: expanded by default
  const [expandedSections, setExpandedSections] = useState<Set<number>>(() => {
    if (userInfo?.role === 'admin' && viewMode === 'admin') {
      const activeIndex = findActiveGroupIndex(location.pathname);
      return activeIndex >= 0 ? new Set([activeIndex]) : new Set();
    }
    return new Set(visibleNavGroups.map((_, i) => i)); // All expanded for other roles
  });

  // Update expanded sections when viewMode changes (for admins switching between views)
  // Note: visibleNavGroups intentionally excluded - it's recreated each render and we only care about viewMode changes
  useEffect(() => {
    if (userInfo?.role === 'admin') {
      if (viewMode === 'admin') {
        const activeIndex = findActiveGroupIndex(location.pathname);
        setExpandedSections(activeIndex >= 0 ? new Set([activeIndex]) : new Set());
      } else {
        setExpandedSections(new Set(visibleNavGroups.map((_, i) => i))); // Expand all
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Auto-expand the active group on route changes (admin view only)
  useEffect(() => {
    if (userInfo?.role === 'admin' && viewMode === 'admin') {
      const activeIndex = findActiveGroupIndex(location.pathname);
      if (activeIndex >= 0) {
        setExpandedSections((prev) => {
          if (prev.has(activeIndex)) return prev;
          return new Set(prev).add(activeIndex);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open (iOS-compatible)
  // Uses useLayoutEffect to run before paint, preventing visible scroll flicker
  useLayoutEffect(() => {
    if (isOpen) {
      pathnameOnOpenRef.current = location.pathname;
      savedScrollRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${savedScrollRef.current}px`;
      document.body.style.width = '100%';
    } else {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      // Only restore scroll for non-navigation closes (Escape, backdrop tap)
      // ScrollToTop handles scroll-to-top on navigation
      if (location.pathname === pathnameOnOpenRef.current) {
        window.scrollTo(0, savedScrollRef.current);
      }
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isOpen, location.pathname]);

  const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
    display: isOpen ? 'block' : 'none',
    transition: 'opacity 0.3s ease-in-out',
    opacity: isOpen ? 1 : 0,
  };

  const drawerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '280px',
    maxHeight: '100dvh',
    height: '100%',
    backgroundColor: 'var(--background-color)',
    boxShadow: 'var(--shadow-lg)',
    transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.3s ease-in-out',
    zIndex: 1000,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 'var(--safe-area-inset-top)',
    paddingBottom: 'var(--safe-area-inset-bottom)',
  };

  const drawerHeaderStyle: React.CSSProperties = {
    padding: '0.5rem 1rem',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
  };

  const closeButtonStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--on-primary-color)',
    fontSize: '1.5rem',
    cursor: 'pointer',
    padding: '0.5rem',
    minHeight: '40px',
    minWidth: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const navListStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overscrollBehavior: 'contain',
  };

  const groupHeaderStyle: React.CSSProperties = {
    width: '100%',
    padding: '1rem 1.5rem 0.5rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '0.5rem',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    textAlign: 'left',
  };

  const chevronStyle: React.CSSProperties = {
    fontSize: '0.6rem',
    marginLeft: 'auto',
    transition: 'transform 0.15s ease',
  };

  const getNavLinkStyle = (linkTo: string): React.CSSProperties => {
    const hasQuery = linkTo.includes('?');
    let isActive: boolean;
    if (hasQuery) {
      const [linkPath, linkQuery] = linkTo.split('?');
      const fullLocation = location.pathname + location.search;
      isActive = fullLocation === linkTo || (location.pathname === linkPath && !location.search && linkQuery?.includes('tab=inbox'));
    } else {
      isActive = location.pathname === linkTo;
    }
    return {
      display: 'flex',
      alignItems: 'center',
      padding: '0.875rem 1.5rem',
      color: isActive ? 'var(--primary-color)' : 'var(--text-primary)',
      textDecoration: 'none',
      borderLeft: isActive ? '4px solid var(--primary-color)' : '4px solid transparent',
      backgroundColor: isActive ? 'var(--surface-color)' : 'transparent',
      fontWeight: isActive ? '500' : 'normal',
      transition: 'all 0.2s',
      gap: '0.75rem',
      minHeight: '40px',
      fontSize: '0.9375rem',
    };
  };

  const userSectionStyle: React.CSSProperties = {
    borderTop: '1px solid var(--border-color)',
    padding: '1rem',
    backgroundColor: 'var(--surface-color)',
  };

  const userInfoStyle: React.CSSProperties = {
    marginBottom: '0.5rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    padding: '0rem 0.5rem',
    borderRadius: 'var(--radius)',
    transition: 'background-color 0.2s',
  };

  const emailStyle: React.CSSProperties = {
    fontWeight: '500',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const roleStyle: React.CSSProperties = {
    fontSize: '0.75rem',
    color: 'var(--text-secondary)',
    textTransform: 'capitalize',
  };

  const logoutButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: 'var(--error-color)',
    color: 'var(--on-primary-color)',
    border: 'none',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    fontWeight: '500',
    minHeight: '40px',
    transition: 'opacity 0.2s',
  };

  return (
    <>
      {/* Backdrop */}
      <div style={backdropStyle} onClick={onClose} aria-hidden="true" />

      {/* Drawer */}
      <nav style={drawerStyle} aria-label="Mobile navigation">
        {/* Header */}
        <div style={drawerHeaderStyle}>
          <span style={{ fontWeight: '600', fontSize: '1.125rem' }}>{t('nav.menu')}</span>
          <button style={closeButtonStyle} onClick={onClose} aria-label="Close menu">
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Navigation Groups */}
        <div style={navListStyle}>
          {visibleNavGroups.map((group, groupIndex) => {
            const isExpanded = expandedSections.has(groupIndex);

            return (
              <div key={groupIndex}>
                {/* Group Header - clickable to expand/collapse */}
                <button
                  style={groupHeaderStyle}
                  onClick={() => toggleSection(groupIndex)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--surface-color)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  aria-expanded={isExpanded}
                  aria-controls={`mobile-nav-section-${groupIndex}`}
                >
                  {group.icon && <group.icon size={16} aria-hidden="true" style={{ color: primaryColor }} />}
                  <span style={{ flex: 1, textAlign: 'left' }}>{group.title}</span>
                  {!isExpanded && group.badge != null && group.badge > 0 && (
                    <span style={countBadge}>
                      {group.badge > 99 ? '99+' : group.badge}
                    </span>
                  )}
                  <span style={chevronStyle}>
                    {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </span>
                </button>

                {/* Child Links - only visible when expanded */}
                {isExpanded && (
                  <div id={`mobile-nav-section-${groupIndex}`} role="region">
                    {group.links.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        style={{ ...getNavLinkStyle(link.to), justifyContent: 'space-between' }}
                        onClick={onClose}
                      >
                        <span>{link.label}</span>
                        {link.badge != null && link.badge > 0 && (
                          <span style={countBadge}>
                            {link.badge > 99 ? '99+' : link.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* User Section */}
        {userInfo && (
          <div style={userSectionStyle}>
            <div
              style={userInfoStyle}
              onClick={() => {
                navigate('/profile');
                onClose();
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate('/profile');
                  onClose();
                }
              }}
              title="View profile"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--background-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div style={emailStyle}>{userInfo.email}</div>
            </div>
            {/* Role and View Mode Switcher Row */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.75rem',
                paddingLeft: '0.5rem',
              }}
            >
              <div style={roleStyle}>{userInfo.role === 'parent' ? t('roles.parent') : userInfo.role}</div>
              {userInfo.role === 'admin' && viewMode && onViewModeSwitch && (
                <ViewSwitcher
                  currentMode={viewMode}
                  onSwitch={(mode) => {
                    onViewModeSwitch(mode);
                    onClose();
                  }}
                  dropUp
                />
              )}
            </div>
            {/* Theme Toggle and Logout */}
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <ThemeToggle compact />
              {onLogout && (
                <button
                  style={{ ...logoutButtonStyle, flex: 1 }}
                  onClick={() => {
                    onLogout();
                    onClose();
                  }}
                >
                  {t('logout')}
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};
